import React from "react";
import { motion } from "framer-motion";
import PosterArt from "./PosterArt";

// The artwork itself is the tap target. No SELECT buttons.
// side: 'left' (crimson confrontation) | 'right' (cyan confrontation)
// state: 'active' | 'chosen' | 'losing'

const ACCENT = {
  left: {
    ring: "rgba(255,42,85,0.9)",
    glow: "0 0 40px rgba(255,42,85,0.45), inset 0 0 24px rgba(255,42,85,0.12)",
    border: "rgba(255,42,85,0.45)",
    idleGlow: "0 0 0px rgba(255,42,85,0)",
  },
  right: {
    ring: "rgba(0,229,255,0.9)",
    glow: "0 0 40px rgba(0,229,255,0.45), inset 0 0 24px rgba(0,229,255,0.12)",
    border: "rgba(0,229,255,0.45)",
    idleGlow: "0 0 0px rgba(0,229,255,0)",
  },
};

export default function ArtworkCard({
  work,
  side,
  state = "active",
  orientation = "portrait",
  onChoose,
  disabled = false,
  reducedMotion = false,
  testId,
  // Reused as-is by GUESS MY TASTE (a guess tap, not a preference tap) —
  // only the label vocabulary differs; the visual primitive doesn't.
  ariaVerb = "Keep",
  badgeLabel = "KEPT",
}) {
  const accent = ACCENT[side];
  const isChosen = state === "chosen";
  const isLosing = state === "losing";

  const animate = reducedMotion
    ? { opacity: isLosing ? 0.35 : 1 }
    : {
        scale: isChosen ? 1.025 : isLosing ? 0.97 : 1,
        opacity: isLosing ? 0.28 : 1,
        filter: isLosing ? "blur(1.5px) saturate(0.7)" : "blur(0px) saturate(1)",
        boxShadow: isChosen ? accent.glow : accent.idleGlow,
      };

  const keptBadge = (
    <motion.div
      aria-hidden
      initial={false}
      animate={{ opacity: isChosen ? 1 : 0, y: isChosen ? 0 : 6 }}
      transition={{ duration: 0.2 }}
      className="absolute top-3 z-20"
      style={{ [side === "left" ? "left" : "right"]: 12 }}
    >
      <span
        className="font-mono text-[10px] font-bold tracking-[0.2em] px-2 py-1 rounded-full"
        style={{
          color: "#05070a",
          background: side === "left" ? "var(--tt-left)" : "var(--tt-right)",
        }}
      >
        {badgeLabel}
      </span>
    </motion.div>
  );

  return (
    <motion.button
      type="button"
      data-testid={testId}
      aria-label={`${ariaVerb} ${work.title}`}
      disabled={disabled}
      onClick={() => onChoose?.(side)}
      initial={false}
      animate={animate}
      transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
      whileTap={reducedMotion || disabled ? undefined : { scale: 0.985 }}
      className={`group relative w-full flex flex-col rounded-2xl overflow-hidden bg-[#0b0c11] focus:outline-none ${orientation === "wide" ? "h-full" : ""}`}
      style={{
        border: `1px solid ${isChosen ? accent.ring : accent.border}`,
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Confrontation edge — restrained, the media stays the hero */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 z-10 rounded-2xl transition-opacity duration-300"
        style={{
          boxShadow: `inset 0 0 0 1px ${accent.border}`,
          opacity: isChosen ? 1 : 0.5,
        }}
      />
      {/* Thin accent bar on the confronting edge */}
      <span
        aria-hidden
        className="pointer-events-none absolute z-10"
        style={{
          background: side === "left" ? "var(--tt-left)" : "var(--tt-right)",
          opacity: isChosen ? 1 : 0.55,
          ...(orientation === "wide"
            ? { left: 0, right: 0, height: 3, [side === "left" ? "top" : "bottom"]: 0 }
            : { top: 0, bottom: 0, width: 3, [side === "left" ? "left" : "right"]: 0 }),
        }}
      />

      {/* POSTER-FIRST media.
          DUEL (portrait): the 2:3 poster fills the card edge-to-edge.
          SHOWDOWN (wide): the media world EXPANDS into the wider card — the SAME
          artwork, enlarged/blurred/darkened, fills the environment while the
          crisp full 2:3 hero poster sits centred and lifted on top. No crop, no
          distortion, no grey filler band, no floating poster in dead space. */}
      {orientation === "wide" ? (
        <div className="relative w-full flex-1 min-h-0 overflow-hidden">
          <PosterArt work={work} variant="background" />
          <div className="relative h-full flex items-center justify-center px-3 py-2.5">
            <div className="relative h-full aspect-[2/3] max-w-full rounded-xl overflow-hidden ring-1 ring-white/12 shadow-[0_14px_44px_rgba(0,0,0,0.62)]">
              <PosterArt work={work} showMeta={false} />
              {keptBadge}
            </div>
          </div>
        </div>
      ) : (
        <div className="relative w-full aspect-[2/3]">
          <PosterArt work={work} showMeta={false} />
          {keptBadge}
        </div>
      )}

      {/* Metadata dock. DUEL stays minimal (title + type · year). SHOWDOWN adds
          an editorial surround — a concise descriptor hook + 2–3 signal chips —
          so the wider card earns its space and answers "why is this matchup
          interesting?" without clutter. */}
      <div className={`relative z-10 w-full text-center ${orientation === "wide" ? "px-4 py-3" : "px-3 py-2.5"}`}>
        <h3 className="font-display font-bold text-[13px] sm:text-sm text-bone leading-tight break-words [overflow-wrap:anywhere] line-clamp-2">
          {work.title}
        </h3>
        <p className="mt-0.5 font-mono text-[9px] tracking-[0.16em] uppercase text-subtle">
          {work.domain} · {work.year}
        </p>
        {orientation === "wide" && work.descriptor && (
          <p className="mt-1.5 font-body text-[11px] leading-snug text-silver max-w-[34ch] mx-auto">
            {work.descriptor}
          </p>
        )}
        {orientation === "wide" && Array.isArray(work.chips) && work.chips.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
            {work.chips.map((c) => (
              <span
                key={c}
                className="font-mono text-[8.5px] font-semibold tracking-[0.14em] uppercase text-bone/85 border border-white/15 bg-white/[0.06] rounded-full px-2 py-[3px]"
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.button>
  );
}
